import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import GroupsForm from '../../components/organizations/GroupsForm/GroupsForm.jsx';
import SubHeader from '../../components/SubHeader.jsx';

import OrganizationHeader from '../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';

import { selectCurrentOrganizationId, selectUser } from '../../modules/auth.js';
import Empty from '../../components/Empty.jsx';
import { loadingContent } from '../../components/EmptyStates.jsx';
import {
  addNode,
  moveNodeToParent,
  onParticipantsUploadChange,
  removeNode,
  saveNodeFieldsChanges,
  validateGroups,
} from './utils/orgStructure.js';
import { flattenedTree, mapFinalFormErrors } from '../../lib/utils.js';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import {
  retrieveOrgGroups,
  selectFlattenedOrgGroups,
  selectOrgGroups,
  selectRetrieveGroupsState,
  updateGroups,
} from '../../modules/orgGroups.js';
import { updateNotificationState } from '../../modules/notifications.js';
import { getOrgTermsContent } from '../../content/utils.js';
import {
  retrieveEmployees,
  selectEmployees,
  selectRetrieveEmployeesState,
} from '../../modules/employees.js';
import { retrieveOrgStructureUploadStatus } from '../../modules/organizations.js';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state) => ({
  organizationId: selectCurrentOrganizationId(state),
  groups: selectOrgGroups(state),
  employees: selectEmployees(state),
  flattenedGroups: selectFlattenedOrgGroups(state),
  retrieveSuccess:
    selectRetrieveGroupsState(state).success &&
    selectRetrieveEmployeesState(state).success,
  user: selectUser(state),
  apiUrl: state.config.apiUrl,
});

const mapDispatchToProps = {
  updateGroups,
  retrieveOrgGroups,
  retrieveEmployees,
  updateNotificationState,
  retrieveOrgStructureUploadStatus,
};

function GroupsFormContainer({
  slug,
  organizationId,
  retrieveOrgGroups,
  employees,
  retrieveSuccess,
  retrieveEmployees,
  updateNotificationState,
  retrieveOrgStructureUploadStatus,
  groups,
  updateGroups,
  apiUrl,
}) {
  const { downloadBlob } = useFetchFile();

  const [groupsTree, setGroupsTree] = useState([]);
  const [groupsFlattened, setGroupsFlattened] = useState([]);

  const [uploadMethod, setUploadMethod] = useState('manually');
  const [replaceMethod, setReplaceMethod] = useState('add');
  const [editableId, setEditableId] = useState(null);

  const formRef = useRef(null);

  const onDownloadCsv = () => {
    downloadBlob(
      apiUrl,
      `organizations/${organizationId}/group_structure_csv`,
      null,
      'current-group-structure CSV',
      'csv'
    );
  };

  const onAddNode = (parentId) => {
    const result = addNode(groupsTree, parentId);
    setGroupsTree(result.tree);
    setGroupsFlattened(flattenedTree(result.tree));
    setEditableId(result.temporaryId);
  };

  const onRemoveNode = (id) => {
    const group = groupsFlattened.find((g) => g.id === id);

    if (
      !window.confirm(
        !group.destroyable
          ? `Are you sure you want to delete this group? \n\nThis group is currently being tracked and used in one and more ${getOrgTermsContent('projects')} and reports. When you delete the group at the organization level, it will be deleted across all ${getOrgTermsContent('projects')} and no longer available for selection in reports.`
          : 'Are you sure you want to delete this group?'
      )
    )
      return;

    const result = removeNode(groupsTree, id);
    setGroupsTree(result);
    setGroupsFlattened(flattenedTree(result));
    setEditableId(null);
  };

  const onSaveNodeFieldsChanges = (prevState, currState) => {
    const movedNodeResult = () => {
      if (prevState.parentId === currState.parentId) return groupsTree;

      return moveNodeToParent(
        groupsTree,
        prevState.id,
        prevState,
        currState.parentId
      );
    };

    const updatedFieldsResult = saveNodeFieldsChanges(
      movedNodeResult(),
      editableId,
      currState
    );

    setGroupsTree(updatedFieldsResult);
    setGroupsFlattened(flattenedTree(updatedFieldsResult));
    setEditableId(null);
  };

  const notify = () => {
    updateNotificationState({
      isShown: true,
      type: 'success',
      forPath: '/organization/show',
      message:
        'Org Structure successfully changed!\n' +
        `We’re updating your active ${getOrgTermsContent('initiatives')} and change impacts to follow the changes you have just uploaded. Items with end date in the past will retain the old structure, naming and emails.\n` +
        'Once ready, we’ll update you via email.',
    });
  };

  const onSubmit = () => {
    const composedData = () => {
      if (uploadMethod === 'csv') {
        const formData = new FormData(formRef.current);
        const composedFormData = new FormData();
        composedFormData.append('manually', false);
        composedFormData.append('merge', replaceMethod === 'add');
        composedFormData.append('csv_file', formData.get('csv_file'));
        return composedFormData;
      }

      return {
        manually: true,
        groups: groupsFlattened,
      };
    };

    return updateGroups(organizationId, composedData())
      .then(() => {
        notify();
        retrieveOrgStructureUploadStatus(organizationId);
        pushRoute(`/${slug}/organization`);
      })
      .catch(mapErrors);
  };

  useEffect(() => {
    retrieveOrgGroups({ active: true });
    retrieveEmployees({ active: true });
  }, []);

  useEffect(() => {
    groups && setGroupsTree(groups);
    groups && setGroupsFlattened(flattenedTree(groups));
  }, [retrieveSuccess]);

  return retrieveSuccess ? (
    <React.Fragment>
      <OrganizationHeader slug={slug} />
      <SubHeader flexJustify small>
        <h2>Edit Organization Structure</h2>
      </SubHeader>
      <Grid fluid className="m-0">
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  <Form
                    slug={slug}
                    formRef={formRef}
                    onSubmit={onSubmit}
                    employeesCount={employees.length}
                    groupsTree={groupsTree}
                    groupsFlattened={groupsFlattened}
                    uploadMethod={uploadMethod}
                    setUploadMethod={(method) =>
                      onParticipantsUploadChange(method, setUploadMethod)
                    }
                    replaceMethod={replaceMethod}
                    setReplaceMethod={setReplaceMethod}
                    editableId={editableId}
                    setEditableId={setEditableId}
                    onAddNode={onAddNode}
                    onRemoveNode={onRemoveNode}
                    onSaveNodeFieldsChanges={onSaveNodeFieldsChanges}
                    validate={() => validateGroups(groupsFlattened)}
                    onDownloadCsv={onDownloadCsv}
                    component={GroupsForm}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  ) : (
    <Empty simplified content={loadingContent} />
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GroupsFormContainer);
